import React, { useEffect, useState } from 'react';
import { ContratoFacade, Contrato, ContratoDTO } from '../../facades/ContratoFacade';
import { ClienteFacade, Cliente } from '../../facades/ClienteFacade';
import Sidebar from '../Sidebar';

export const ContratosList: React.FC = () => {
    const [contratos, setContratos] = useState<Contrato[]>([]);
    const [clientesCombo, setClientesCombo] = useState<Cliente[]>([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [sucesso, setSucesso] = useState<string | null>(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editandoId, setEditandoId] = useState<number | null>(null);
    const [formData, setFormData] = useState<ContratoDTO>({
        numeroContrato: '', objeto: '', status: 'Vigente', dataInicio: '', dataFim: '', valorTotal: 0, clienteId: 0
    });
    const [salvando, setSalvando] = useState(false);

    const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null as number | null, numero: '' });

    useEffect(() => {
        if (sucesso || erro) {
            const timer = setTimeout(() => { setSucesso(null); setErro(null); }, 5000);
            return () => clearTimeout(timer);
        }
    }, [sucesso, erro]);

    useEffect(() => {
        carregarDadosBase();
    }, []);

    const carregarDadosBase = async () => {
        try {
            setLoading(true);
            const [contratosData, clientesData] = await Promise.all([
                ContratoFacade.listarTodos(),
                ClienteFacade.listarTodos()
            ]);
            setContratos(contratosData || []);
            setClientesCombo(clientesData || []);
        } catch (error) {
            setContratos([]);
            setClientesCombo([]);
            setErro("Sessão expirada ou acesso negado. Por favor, faça login novamente.");
        } finally {
            setLoading(false);
        }
    };

    const formatarMoeda = (valor: number | null | undefined) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
    };

    const formatarData = (dataSql: string | null | undefined) => {
        if (!dataSql) return "-";
        const partes = dataSql.split('-');
        if (partes.length !== 3) return dataSql;
        return `${partes[2]}/${partes[1]}/${partes[0]}`;
    };

    const abrirModalNovo = () => {
        setErro(null); setSucesso(null);
        setFormData({ numeroContrato: '', objeto: '', status: 'Vigente', dataInicio: '', dataFim: '', valorTotal: 0, clienteId: 0 });
        setEditandoId(null);
        setIsModalOpen(true);
    };

    const abrirModalEditar = (contrato: Contrato) => {
        setErro(null); setSucesso(null);
        setFormData({
            numeroContrato: contrato.numeroContrato || '',
            objeto: contrato.objeto || '',
            status: contrato.status || 'Vigente',
            dataInicio: contrato.dataInicio || '',
            dataFim: contrato.dataFim || '',
            valorTotal: contrato.valorTotal || 0,
            clienteId: contrato.cliente?.id || 0
        });
        setEditandoId(contrato.id);
        setIsModalOpen(true);
    };

    const handleSalvar = async (e: React.FormEvent) => {
        e.preventDefault();
        setErro(null); setSucesso(null);

        if (formData.clienteId === 0) {
            setErro("Selecione o Cliente associado ao contrato.");
            return;
        }

        try {
            setSalvando(true);
            if (editandoId) {
                await ContratoFacade.atualizar(editandoId, formData);
                setSucesso("Contrato atualizado com sucesso!");
            } else {
                await ContratoFacade.criar(formData);
                setSucesso("Contrato cadastrado com sucesso!");
            }
            setIsModalOpen(false);
            await carregarDadosBase();
        } catch (error: any) {
            setErro("Erro ao salvar o contrato. Verifique os dados informados.");
        } finally {
            setSalvando(false);
        }
    };

    const confirmarExclusao = async () => {
        if (!deleteModal.id) return;
        try {
            await ContratoFacade.excluir(deleteModal.id);
            setSucesso(`Contrato ${deleteModal.numero} excluído com sucesso!`);
            setDeleteModal({ isOpen: false, id: null, numero: '' });
            await carregarDadosBase();
        } catch (error) {
            setErro("Erro ao excluir. O servidor recusou a ação.");
            setDeleteModal({ isOpen: false, id: null, numero: '' });
        }
    };

    return (
        <Sidebar>
            <header className="mb-8 border-b-2 border-gray-200 pb-4 flex justify-between items-end flex-wrap gap-4">
                <div>
                    <h2 className="text-3xl font-extrabold text-gray-800 tracking-tight">Instrumentos Contratuais</h2>
                    <p className="text-gray-500 font-medium mt-1">Gerencie vigências, objetos e valores de contratos públicos.</p>
                </div>
                <button onClick={abrirModalNovo} className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition-all flex items-center gap-2 whitespace-nowrap">
                    <span>📄</span> Novo Contrato
                </button>
            </header>

            <div className="min-h-[70px]">
                {erro && (<div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-6 shadow-sm flex items-center justify-between"><div className="flex items-center gap-3"><span className="text-2xl">⚠️</span><p className="font-semibold">{erro}</p></div><button onClick={() => setErro(null)} className="text-xl font-bold">&times;</button></div>)}
                {sucesso && (<div className="bg-green-50 border-l-4 border-green-500 text-green-800 p-4 rounded-lg mb-6 shadow-sm flex items-center justify-between"><div className="flex items-center gap-3"><span className="text-2xl">✅</span><p className="font-semibold">{sucesso}</p></div><button onClick={() => setSucesso(null)} className="text-xl font-bold">&times;</button></div>)}
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20"><div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-900 mb-4"></div><p className="text-xl text-blue-900 font-bold animate-pulse">Sincronizando contratos...</p></div>
            ) : (
                <div className="bg-white rounded-2xl shadow-lg overflow-x-auto border border-gray-100">
                    <table className="min-w-full text-left text-sm whitespace-nowrap">
                        <thead className="uppercase tracking-wider border-b-2 border-gray-200 bg-slate-50 text-gray-600 font-black">
                        <tr>
                            <th className="px-6 py-4">Nº Contrato</th>
                            <th className="px-6 py-4">Órgão / Cliente</th>
                            <th className="px-6 py-4">Vigência</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Valor Total</th>
                            <th className="px-6 py-4 text-center">Ações</th>
                        </tr>
                        </thead>
                        <tbody>
                        {!Array.isArray(contratos) || contratos.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-8 text-gray-500 italic">Nenhum contrato localizado na base de dados.</td></tr>
                        ) : (
                            contratos.map((cont) => (
                                <tr key={cont.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                    <td className="px-6 py-4 font-mono font-bold text-blue-900 text-base">{cont.numeroContrato || '-'}</td>
                                    <td className="px-6 py-4 font-bold text-gray-700">🏢 {cont.cliente?.nome || '-'}</td>
                                    <td className="px-6 py-4 text-gray-600 font-medium">
                                        {formatarData(cont.dataInicio)} <span className="text-gray-400 mx-1">até</span> {formatarData(cont.dataFim)}
                                    </td>
                                    <td className="px-6 py-4">
                                            <span className={`py-1 px-3 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                cont.status === 'Vigente' ? 'bg-green-100 text-green-700' :
                                                    cont.status === 'Vencido' ? 'bg-red-100 text-red-700' :
                                                        cont.status === 'Suspenso' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-200 text-gray-700'
                                            }`}>
                                                {cont.status || '-'}
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 font-bold text-gray-800">{formatarMoeda(cont.valorTotal)}</td>
                                    <td className="px-6 py-4 text-center">
                                        <button onClick={() => abrirModalEditar(cont)} className="text-blue-600 hover:text-blue-900 font-bold mr-4">Editar</button>
                                        <button onClick={() => setDeleteModal({ isOpen: true, id: cont.id, numero: cont.numeroContrato })} className="text-red-500 hover:text-red-700 font-bold">Excluir</button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden my-8">
                        <div className="bg-blue-900 p-5 flex justify-between items-center border-b border-yellow-400">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                {editandoId ? '✏️ Alterar Contrato' : '📄 Registrar Novo Contrato'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-blue-200 hover:text-white font-bold text-2xl leading-none">&times;</button>
                        </div>

                        <form onSubmit={handleSalvar} className="p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                                <div className="col-span-1 sm:col-span-2 md:col-span-3">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Órgão / Cliente Contratante</label>
                                    <select
                                        required
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900 font-semibold"
                                        value={formData.clienteId}
                                        onChange={(e) => setFormData({...formData, clienteId: Number(e.target.value)})}
                                    >
                                        <option value="0" disabled>-- Selecione na base de clientes --</option>
                                        {Array.isArray(clientesCombo) && clientesCombo.map(c => (
                                            <option key={c.id} value={c.id}>{c.nome} (CNPJ/CPF: {c.cpfCnpj})</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-span-1 md:col-span-1">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Número do Contrato</label>
                                    <input type="text" required maxLength={50} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900 font-mono font-bold" value={formData.numeroContrato} onChange={(e) => setFormData({...formData, numeroContrato: e.target.value})} placeholder="Ex: 045/2026" />
                                </div>

                                <div className="col-span-1 md:col-span-1">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Status</label>
                                    <select className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900 font-bold" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                                        <option value="Vigente">🟢 Vigente</option>
                                        <option value="Encerrado">⚪ Encerrado</option>
                                        <option value="Vencido">🔴 Vencido</option>
                                        <option value="Suspenso">🟡 Suspenso</option>
                                    </select>
                                </div>

                                <div className="col-span-1 md:col-span-1">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Valor Total (R$)</label>
                                    <input type="number" step="0.01" min="0" required className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900" value={formData.valorTotal || ''} onChange={(e) => setFormData({...formData, valorTotal: parseFloat(e.target.value)})} placeholder="0.00" />
                                </div>

                                <div className="col-span-1 sm:col-span-2 md:col-span-3">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Objeto do Contrato</label>
                                    <textarea required rows={2} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900" value={formData.objeto} onChange={(e) => setFormData({...formData, objeto: e.target.value})} placeholder="Descreva o objeto da contratação..." />
                                </div>

                                <div className="col-span-1 md:col-span-1">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Data de Início</label>
                                    <input type="date" required className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900" value={formData.dataInicio} onChange={(e) => setFormData({...formData, dataInicio: e.target.value})} />
                                </div>

                                <div className="col-span-1 md:col-span-1">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Data Final (Término)</label>
                                    <input type="date" required className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900" value={formData.dataFim} onChange={(e) => setFormData({...formData, dataFim: e.target.value})} />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-4">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 text-gray-600 font-bold rounded-lg hover:bg-gray-100 transition-colors">Cancelar</button>
                                <button type="submit" disabled={salvando} className="px-6 py-2.5 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-lg shadow-md transition-all disabled:opacity-50">
                                    {salvando ? '⏳ Processando...' : '💾 Salvar Contrato'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {deleteModal.isOpen && (
                <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden text-center p-6">
                        <h3 className="text-2xl font-black text-gray-800 mb-2">Excluir Contrato</h3>
                        <p className="text-gray-600 mb-6">Deseja remover o contrato número <br/><strong className="text-gray-900">{deleteModal.numero}</strong> da base?</p>
                        <div className="flex flex-col sm:flex-row-reverse gap-3">
                            <button onClick={confirmarExclusao} className="w-full sm:w-auto rounded-lg bg-red-600 px-6 py-3 font-bold text-white hover:bg-red-700 transition-all">Sim, excluir</button>
                            <button onClick={() => setDeleteModal({ isOpen: false, id: null, numero: '' })} className="w-full sm:w-auto rounded-lg border border-gray-300 px-6 py-3 font-bold text-gray-700 hover:bg-gray-50 transition-all">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
        </Sidebar>
    );
};